
import React, { useState, useEffect, useRef } from 'react';
import { LobbyChatMessage } from '../types';
import { getAsset } from '../services/assets';
import { Language, t } from '../data/translations';

interface LobbyChatProps {
    messages: LobbyChatMessage[];
    onSend: (text: string) => void;
    localPlayerId: string;
    language: Language;
}

const LobbyChat: React.FC<LobbyChatProps> = ({ messages, onSend, localPlayerId, language }) => {
    const [inputText, setInputText] = useState('');
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (inputText.trim()) {
            onSend(inputText.trim());
            setInputText('');
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (inputText.trim()) {
                onSend(inputText.trim());
                setInputText('');
            }
        }
    }

    // Emoji picker could be a simple list of buttons for now
    const emojis = ['😀', '😎', '👍', '🔥', '💀', '😱', '👋', '⚔️'];

    return (
        <div className="flex flex-col h-full bg-slate-900/60 rounded-xl border border-slate-700/50 overflow-hidden shadow-inner backdrop-blur-sm">
            {/* Header */}
            <div className="p-3 bg-slate-800/80 border-b border-slate-700 font-bold text-slate-200 tracking-wide text-sm uppercase flex items-center gap-2">
                <span>💬</span> {t('ui.lobbyChat', language)}
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-3 custom-scrollbar">
                {messages.length === 0 && (
                    <div className="text-center text-slate-500 text-sm mt-10 italic">
                        {t('ui.sayHello', language)}
                    </div>
                )}
                {messages.map((msg, idx) => {
                    const isMe = msg.senderId === localPlayerId;
                    return (
                        <div key={idx} className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>
                            <div className={`text-xs mb-1 ${isMe ? 'text-cyan-400' : 'text-amber-400'}`}>
                                {msg.senderName}
                                <span className="text-slate-600 ml-2 text-[10px]">{new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                            </div>
                            <div className={`max-w-[85%] px-3 py-2 rounded-lg text-sm break-words ${isMe
                                ? 'bg-cyan-900/40 text-cyan-100 border border-cyan-800/50 rounded-tr-none'
                                : 'bg-slate-800/60 text-slate-200 border border-slate-700 rounded-tl-none'
                                }`}>
                                {msg.text}
                            </div>
                        </div>
                    );
                })}
                <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="p-3 bg-slate-800/50 border-t border-slate-700">
                <form onSubmit={handleSubmit} className="flex flex-col gap-2">
                    <div className="flex gap-1 justify-center">
                        {emojis.map(emoji => (
                            <button
                                key={emoji}
                                type="button"
                                onClick={() => setInputText(prev => prev + emoji)}
                                className="w-8 h-8 flex items-center justify-center hover:bg-slate-700 active:bg-slate-600 rounded transition-colors text-lg"
                            >
                                {emoji}
                            </button>
                        ))}
                    </div>
                    <div className="flex gap-2">
                        <input
                            type="text"
                            value={inputText}
                            onChange={(e) => setInputText(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={t('ui.typeMessage', language)}
                            className="flex-1 bg-slate-900 border border-slate-600 rounded px-3 py-2 text-white text-sm focus:outline-none focus:ring-1 focus:ring-cyan-500 placeholder-slate-500"
                            autoComplete="off"
                        />
                        <button
                            type="submit"
                            disabled={!inputText.trim()}
                            className="bg-cyan-700 hover:bg-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded text-sm font-medium transition-colors"
                        >
                            {t('ui.send', language)}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default LobbyChat;
